import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Instagram, MapPin, Maximize2, PlayCircle } from "lucide-react";
import { PageShell, VideoPlayer, mediaUrl, youtubeId } from "../components/legend/PageShell";
import { Lightbox, useLightbox } from "../components/legend/Lightbox";
import { useEventi } from "../components/legend/NewsTicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoStrip = ({ title, logos, testid }) =>
  logos.length > 0 && (
    <div data-testid={testid} className="flex flex-wrap items-center gap-3">
      <span className="mono-label text-[10px] uppercase tracking-wider text-neutral-500 w-full sm:w-auto">{title}</span>
      {logos.map((l, i) => (
        <span key={i} className="bg-white border border-neutral-800 px-3 py-2 flex items-center" title={l.didascalia}>
          <img src={mediaUrl(l.url)} alt={l.didascalia || title} className="h-10 w-auto object-contain" loading="lazy" />
        </span>
      ))}
    </div>
  );

export const EventCard = ({ e }) => (
  <Link
    to={`/gare/${e.slug}`}
    data-testid={`evento-card-${e.slug}`}
    className="group relative block aspect-[4/3] border border-neutral-800 hover:border-red-600 overflow-hidden bg-neutral-900 transition-colors"
  >
    {(e.copertina || e.media?.[0]?.tipo === "image") && (
      <img src={mediaUrl(e.copertina || e.media[0].url)} alt={e.titolo} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
    )}
    <span className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-neutral-950 to-transparent">
      <span className="mono-label text-[10px] text-red-500 uppercase tracking-wider block">{e.anno} &bull; {e.luogo || "Rally"}</span>
      <span className="font-display text-xl font-black uppercase text-white block">{e.titolo}</span>
      <span className="mono-label text-[10px] text-neutral-400 block mt-1">{e.media?.length || 0} contenuti</span>
    </span>
  </Link>
);

export default function Gare() {
  const eventi = useEventi();
  return (
    <PageShell testid="gare-page" eyebrow="Galleria gare" title="Le gare del Legend Team" subtitle="Luoghi, eventi, foto e video di ogni rally: scegli una gara per aprire la sua pagina dedicata.">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {eventi.map((e) => <EventCard key={e.id} e={e} />)}
      </div>
    </PageShell>
  );
}

export const instagramUrl = (v) => (!v ? "" : /^https?:\/\//i.test(v) ? v : `https://www.instagram.com/${v.replace(/^@/, "")}/`);
export const instagramHandle = (v) => (!v ? "" : `@${v.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/^@/, "").replace(/\/.*$/, "")}`);

/** Team e piloti ospiti che hanno corso con il Legend Team: credit Instagram + foto ingrandibili. */
const TeamSection = ({ team, evento }) => {
  const lb = useLightbox();
  const foto = team.flatMap((t) => t.foto.filter((m) => m.tipo === "image").map((m) => ({ src: mediaUrl(m.url), label: m.didascalia || `${t.nome} — ${evento}` })));
  let offset = 0;
  return (
    <section data-testid="gara-team" className="border border-neutral-800 bg-neutral-900 p-5 space-y-6">
      <div>
        <span className="mono-label text-[10px] uppercase tracking-wider text-red-500">Hanno corso con noi</span>
        <h2 className="font-display text-base md:text-lg font-black uppercase text-white">Team e piloti ospiti</h2>
      </div>
      {team.map((t, i) => {
        const imgs = t.foto.filter((m) => m.tipo === "image");
        const start = offset;
        offset += imgs.length;
        return (
          <div key={i} data-testid={`team-${i}`} className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="font-display text-xl font-black uppercase text-white">{t.nome}</span>
              {t.ruolo && <span className="mono-label text-[10px] uppercase tracking-wider text-neutral-500">{t.ruolo}</span>}
              {t.instagram && (
                <a href={instagramUrl(t.instagram)} target="_blank" rel="noopener noreferrer" data-testid={`team-${i}-instagram`} className="inline-flex items-center gap-1.5 mono-label text-[11px] text-neutral-300 hover:text-red-500 transition-colors">
                  <Instagram className="w-3.5 h-3.5" /> {instagramHandle(t.instagram)}
                </a>
              )}
            </div>
            {imgs.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {imgs.map((m, k) => (
                  <button key={k} type="button" onClick={() => lb.open(start + k)} className="group relative aspect-[4/3] border border-neutral-800 overflow-hidden cursor-zoom-in">
                    <img src={mediaUrl(m.url)} alt={m.didascalia || t.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <span className="absolute top-2 right-2 w-8 h-8 bg-neutral-950/80 border border-neutral-700 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-4 h-4" /></span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-500">Foto del team in arrivo.</p>
            )}
          </div>
        );
      })}
      <Lightbox items={foto} index={lb.index} onClose={lb.close} onChange={lb.change} />
    </section>
  );
};

export function GaraDettaglio() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [err, setErr] = useState(false);
  const lb = useLightbox();
  useEffect(() => {
    axios.get(`${API}/eventi/${slug}`).then((r) => setE(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <PageShell testid="gara-404" title="Pagina non trovata" back="/gare" />;
  if (!e) return <PageShell testid="gara-loading" title="Caricamento..." back="/gare" />;
  const foto = e.media.filter((m) => m.tipo === "image").map((m) => ({ src: mediaUrl(m.url), label: m.didascalia || e.titolo }));
  const video = e.media.filter((m) => m.tipo !== "image");

  return (
    <PageShell testid="gara-page" eyebrow={`Rally ${e.anno}`} title={e.titolo} subtitle={e.descrizione} back="/gare">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="flex flex-wrap items-center gap-6 mono-label text-xs uppercase tracking-wider text-neutral-400">
          {e.luogo && <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-red-500" /> {e.luogo}</span>}
          {e.data && <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-red-500" /> {e.data}</span>}
        </div>
        <div className="space-y-4 border border-neutral-800 bg-neutral-900 p-5">
          <LogoStrip title="Organizzatori" logos={e.loghi_organizzatori} testid="loghi-organizzatori" />
          <LogoStrip title="Comuni" logos={e.loghi_comuni} testid="loghi-comuni" />
          {!e.loghi_organizzatori.length && !e.loghi_comuni.length && (
            <p className="text-xs text-neutral-500">Loghi di organizzatori e comuni in arrivo.</p>
          )}
        </div>

        {video.length > 0 && (
          <div data-testid="gara-video" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {video.map((m, i) => (
              <figure key={i} className="border border-neutral-800">
                <VideoPlayer media={m} />
                {m.didascalia && <figcaption className="p-3 mono-label text-[11px] uppercase text-neutral-400">{m.didascalia}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        {e.team?.length > 0 && <TeamSection team={e.team} evento={e.titolo} />}

        {foto.length > 0 ? (
          <div data-testid="gara-foto" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {foto.map((f, i) => (
              <button key={i} type="button" onClick={() => lb.open(i)} className="group relative aspect-[4/3] border border-neutral-800 overflow-hidden cursor-zoom-in">
                <img src={f.src} alt={f.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <span className="absolute top-2 right-2 w-8 h-8 bg-neutral-950/80 border border-neutral-700 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-4 h-4" /></span>
              </button>
            ))}
          </div>
        ) : (
          <p data-testid="gara-empty" className="border border-dashed border-neutral-800 p-10 text-center text-sm text-neutral-500">
            Foto e video di questa gara verranno pubblicati dalla segreteria Legend Team.
          </p>
        )}
      </div>
      <Lightbox items={foto} index={lb.index} onClose={lb.close} onChange={lb.change} />
    </PageShell>
  );
}

export const videoThumb = (m) => (m?.tipo === "youtube" && youtubeId(m.url) ? `https://i.ytimg.com/vi/${youtubeId(m.url)}/hqdefault.jpg` : null);
export { PlayCircle };
